import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, BuildingLibraryIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card } from '@/components/common';
import api, { getErrorMessage } from '@/services/api';

/** One product row from GET /brokers/policy-requests/available_policies/.
 *
 *  `id` is the POLICY id (the access row is gone — access is allow-by-default and
 *  BrokerPolicyAccess is now only a disable-list). `access_status` is exactly two
 *  states: 'available' or 'disabled'.
 */
interface AvailableProduct {
  id: string;
  insurer: string;
  insurer_name: string;
  name: string;
  code: string;
  category: string;
  is_active: boolean;
  access_status: 'available' | 'disabled' | string;
}

export function PolicyAccessPage() {
  const queryClient = useQueryClient();

  const { data: products = [], isLoading, isFetching, refetch } = useQuery<AvailableProduct[]>({
    // Deliberately the AVAILABLE list, not my_policies: available_policies still
    // lists switched-off products (marked `disabled`), and it is the only screen
    // from which a broker can switch one back on.
    queryKey: ['brokerAvailableProducts'],
    queryFn: async () => {
      const res = await api.get('/brokers/policy-requests/available_policies/');
      return res.data;
    },
  });

  const togglePolicy = useMutation({
    mutationFn: async ({ policyId, enable }: { policyId: string; enable: boolean }) => {
      await api.post('/brokers/policy-requests/toggle_policy/', { policy_id: policyId, enable });
      return enable;
    },
    onSuccess: (enable) => {
      toast.success(enable ? 'Product enabled' : 'Product disabled');
      queryClient.invalidateQueries({ queryKey: ['brokerAvailableProducts'] });
    },
    // A product the INSURER switched off also reads as 'disabled', and the server
    // refuses to re-enable it (403). Surface that refusal rather than guessing.
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const togglingId = togglePolicy.isPending ? togglePolicy.variables?.policyId : null;

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle="Every product your insurance companies publish is yours to sell. Switch one off if you do not want to transact on it."
        breadcrumbs={[{ label: 'Broker', href: '/dashboard' }, { label: 'Products' }]}
        actions={
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => refetch()}
            isLoading={isFetching && !isLoading}
          >
            Refresh
          </Button>
        }
      />

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton />
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900">No products yet</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
              Products appear here as soon as you are connected to an insurance company that has
              published them. If you are already connected and still see nothing, ask your country
              admin to activate the insurer and publish its products.
            </p>
            <div className="mt-4">
              <Link to="/broker/insurers">
                <Button leftIcon={<BuildingLibraryIcon className="w-4 h-4" />}>
                  Manage Insurer Connections
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Product', 'Insurer', 'Category', 'Status'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((product) => {
                  const disabled = product.access_status === 'disabled';
                  return (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.code}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{product.insurer_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{product.category || '-'}</td>
                      <td className="px-6 py-4">
                        <Badge variant={disabled ? 'default' : 'success'} size="sm">
                          {disabled ? 'Disabled' : 'Available'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant={disabled ? 'primary' : 'outline'}
                          size="sm"
                          isLoading={togglingId === product.id}
                          onClick={() =>
                            togglePolicy.mutate({ policyId: product.id, enable: disabled })
                          }
                        >
                          {disabled ? 'Enable' : 'Disable'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 rounded-t-lg" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 border-t border-gray-100 flex items-center px-6 gap-4">
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-40 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-24" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-28" />
          <div className="h-4 bg-gray-200 rounded w-20" />
          <div className="h-6 bg-gray-200 rounded-full w-20" />
          <div className="h-8 bg-gray-200 rounded w-24" />
        </div>
      ))}
    </div>
  );
}
